import { ReactNode, useCallback, useMemo } from 'react';
import { useCollapse } from 'react-collapsed';
import { useMount } from 'react-use';
import { isFunction } from 'lodash-es';
import cn from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { MdKeyboardArrowDown, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';
import { Button } from '@ds';
import { ServiceRulesViolations } from '@lp/features/Scheduler/types';
import { getDailyAndWeeklyServiceViolations } from '@lp/features/Scheduler/validation.utils';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { useCSSchedulerStore, csSchedulerSelectors } from '@lp/core/store/scheduler';
import { Region } from '@/api/apis/resources/schedule';

type RegionWrapperProps = {
  region: Region;
  withTotals?: boolean;
  initiallyExpanded?: boolean;
  violations?: ServiceRulesViolations;
  children?: ReactNode | ((showHubTotals?: boolean) => ReactNode);
};

export const RegionWrapper = ({
  region,
  withTotals,
  initiallyExpanded = false,
  violations,
  children,
}: RegionWrapperProps) => {
  const { isExpanded, totalsOpened } = useCSSchedulerStore(useShallow(csSchedulerSelectors.getRegionState(region.id)));
  const setRegionExpand = useCSSchedulerStore.use.setRegionExpand();
  const setRegionTotalsOpen = useCSSchedulerStore.use.setRegionTotalsOpen();
  const violationList = useMemo(() => getDailyAndWeeklyServiceViolations(violations), [violations]);
  const defaultExpanded = initiallyExpanded && isExpanded === undefined;
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded: !!isExpanded, defaultExpanded });

  const renderChildren = useCallback(() => {
    return isFunction(children) ? children(totalsOpened) : children;
  }, [children, totalsOpened]);

  useMount(() => {
    if (initiallyExpanded && isExpanded === undefined) setRegionExpand(region.id, true);
  });

  return (
    <div>
      <button
        {...getToggleProps({ onClick: () => setRegionExpand(region.id) })}
        className="flex items-center gap-2"
        type="button"
      >
        <div className="flex items-center justify-center size-4 rounded-full bg-[#111]">
          <MdKeyboardArrowDown size={16} className={cn('text-base-90 transition', { '-rotate-90': !isExpanded })} />
        </div>
        <h2 className="text-hm text-[#111]">{region.name}</h2>
        {violationList.length > 0 && (
          <ScheduleViolationTooltip items={violationList} withLabel>
            <div className="px-1.5 h-4 rounded-lg bg-danger-30 text-white text-s-sb">{violationList.length}</div>
          </ScheduleViolationTooltip>
        )}
      </button>
      <div {...getCollapseProps()}>
        <div className="flex flex-col gap-5 pt-5">
          {isExpanded ? renderChildren() : null}
          {withTotals && (
            <div className="flex justify-end">
              <Button
                disableRipple
                size="sm"
                variant="light"
                className="-mt-2"
                startContent={totalsOpened ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
                onClick={() => setRegionTotalsOpen(region.id)}
              >
                {totalsOpened ? 'Hide' : 'Show'} Region Totals
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
